<template>
  <div :class="'table'">
    <div
      v-if="parsedCaption && parsedCaption.length > 0"
      class="table__headline"
    >
      <TheContent
        v-for="caption of parsedCaption"
        :key="caption.id"
        :elem="caption"
      />
    </div>
    <div
      class="table__container"
      :class="tableContainerClass"
      @mousedown="dragStart"
      @mousemove="scrollOnDrag"
      @mouseup="stopScrollOnDrag"
      @mouseleave="stopScrollOnDrag"
    >
      <div
        ref="tableWrapp"
        class="table__wrapp"
        :class="tableWrappClass"
        @scroll="updateScrollLeft"
      >
        <table class="table__content">
          <TheContent
            v-for="child of children"
            :key="child.id"
            :elem="child"
          />
        </table>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import type { PropOptions } from 'vue';
  import { isMobile } from 'mobile-device-detect';
  import { BREAKPOINTS } from '~/constants';
  import TheContent from '~/components/TheContent';
  import type { TContent } from '~/types/common';
  import { useWindowStore } from '~/store/window';

  export default defineNuxtComponent({
    name: 'TheTable',

    components: {
      TheContent,
    },

    props: {
      elem: {
        type: Object,
        required: true,
      } as PropOptions<TContent>,
    },

    setup() {
      const tableWrapRef = useTemplateRef<HTMLDivElement>('tableWrapp');
      const { width } = useElementSize(tableWrapRef);
      const hasScroll = ref(false);

      watch(
        width,
        () => {
          hasScroll.value =
            (tableWrapRef.value?.offsetWidth || 0) < (tableWrapRef.value?.scrollWidth || 0);
        },
        { immediate: import.meta.client },
      );

      return { hasScroll };
    },

    data() {
      return {
        table: null as HTMLElement | null,
        delayAutoScrollTimer: null as number | null,
        autoScrollTimer: null as number | null,
        scrollLeft: 0 as number,
        isMobile,
        draggable: false as boolean,
        startPosX: 0,
        lastPosX: 0,
        autoScrollStep: 20,
        autoScrollStartDelay: 300,
        autoScrollDelay: 100,
      };
    },

    computed: {
      windowWidth() {
        return useWindowStore().windowWidth;
      },

      parsedCaption(): TContent[] | null {
        return this.elem.attributes.parsed_caption;
      },

      children(): TContent[] | null {
        return this.elem.children;
      },

      tableWrappClass(): Record<string, boolean> {
        return { 'table__wrapp--scroll': this.hasScroll };
      },

      tableContainerClass(): object {
        return {
          'table__container--fade-right': !this.isRightBtnDisabled && this.isShowButtons,

          'table__container--fade-left': !this.isLeftBtnDisabled && this.isShowButtons,
        };
      },

      isShowButtons(): boolean {
        return (
          (this.table?.offsetWidth || 0) < (this.table?.scrollWidth || 0) &&
          this.windowWidth >= BREAKPOINTS.TABLET
        );
      },

      isLeftBtnDisabled(): boolean {
        if (this.table) {
          return this.scrollLeft <= this.table?.offsetWidth;
        }
        return true;
      },

      isRightBtnDisabled(): boolean {
        if (this.table) {
          return this.scrollLeft >= this.table?.scrollWidth;
        }
        return true;
      },
    },

    methods: {
      dragStart(e: MouseEvent) {
        if (!this.isMobile) {
          this.draggable = true;
          this.startPosX = e.clientX;
        }
      },

      scrollOnDrag(e: MouseEvent) {
        if (this.draggable) {
          // @ts-expect-error ref typing is not inferred in options API
          const tableWrapp = this.$refs.tableWrapp as HTMLElement;
          // @ts-expect-error computed assignment intended
          const dX = -this.startPosX + (this.startPosX = e.clientX);
          tableWrapp.scrollLeft -= dX;
        }
      },

      stopScrollOnDrag() {
        this.draggable = false;
        this.startPosX = 0;
      },

      updateScrollLeft() {
        const scrollWidth = this.table?.scrollWidth || 0;
        const offsetWidth = this.table?.offsetWidth || 0;
        // @ts-expect-error ref typing is not inferred in options API
        this.scrollLeft = this.table?.scrollLeft + offsetWidth;
        if (this.scrollLeft <= offsetWidth || this.scrollLeft >= scrollWidth) {
          this.scrollLeft = this.scrollLeft <= offsetWidth ? offsetWidth : scrollWidth;
          this.stopScroll();
        }
      },

      startAutoScroll(direction: string): void {
        this.delayAutoScrollTimer = window.setTimeout(() => {
          this.autoScrollTimer = window.setInterval(() => {
            this.scrollTo(this.autoScrollStep, direction);
          }, this.autoScrollDelay);
        }, this.autoScrollStartDelay);
      },

      stopScroll(): void {
        window.setTimeout(() => {
          if (this.autoScrollTimer !== null) {
            window.clearInterval(this.autoScrollTimer);
          }
          if (this.delayAutoScrollTimer !== null) {
            window.clearTimeout(this.delayAutoScrollTimer);
          }
          this.autoScrollTimer = null;
          this.delayAutoScrollTimer = null;
          if (this.table) {
            this.table.style.scrollBehavior = '';
          }
        });
      },

      applyScroll(direction: string): void {
        if (!this.autoScrollTimer) {
          this.scrollTo(this.table?.offsetWidth || 0, direction);
        }
      },

      scrollTo(scrollStep: number, direction: string): void {
        scrollStep = direction === 'next' ? scrollStep : -scrollStep;

        if (this.table) {
          this.table.style.scrollBehavior = 'smooth';
          this.table?.scrollBy(scrollStep, 0);
          this.table.style.scrollBehavior = '';
        }
      },
    },
  });
</script>

<style lang="scss" scoped>
  @use '@devhacker/shared/styles/scrollbar.scss' as scrollbar;

  .table {
    $tableWrapp-padding-bottom: $spacing-2;
    $tableWrapp-fade-height: 4px;
    overflow: hidden;
    font-family: $text-font--1;
    @include smallText;
    position: relative;
    margin-left: -16px;
    margin-right: -16px;
    :deep() a {
      @include link-styles;
    }
    &__headline {
      margin-bottom: $spacing-2;
      font-family: $text-font--1;
      @include normalText;
      font-weight: 500;
      padding: 0 16px;

      @include tablet {
        @include mediumText;
      }
    }

    &__container {
      position: relative;
      display: grid;
      &::before,
      &::after {
        position: absolute;
        content: '';
        background: $stroke-color--1;
        top: 0;
        width: 1px;
        height: calc(100% - 12px);
        z-index: 1;
        display: none;
        @include tablet {
          display: block;
        }
      }

      &::before {
        left: 0;
      }

      &::after {
        right: 0;
      }

      @include tablet {
        &::before {
          left: 0;
          z-index: 1;
          background: linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
        }

        &::after {
          right: 0;
          background: linear-gradient(270deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
        }

        &::after,
        &::before {
          position: absolute;
          content: '';
          height: calc(100% - 12px);
          width: 24px;
          top: 0;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease-in;
        }

        &--fade-right {
          &::after {
            opacity: 1;
          }
        }

        &--fade-left {
          &::before {
            opacity: 1;
          }
        }
      }
    }

    &__button {
      position: absolute;
      top: 50%;
      transition: opacity 0.2s linear;
      z-index: 2;

      &:disabled {
        display: none;
      }

      &:not(:disabled) {
        cursor: pointer;
      }

      &--left {
        left: 18px;
        transform: scaleX(-1) translateY(-50%);
      }

      &--right {
        right: 18px;
        transform: translateY(-50%);
      }
    }

    &__wrapp {
      overflow-x: auto;
      position: relative;
      margin: 0 16px;

      --scrollbar-thumb-color: black;
      --scrollbar-track-background: #{$border-color--2};
      --scrollbar-thumb-hover-color: #{$hover-color--1};
      --scrollbar-height: 4px;
      --scrollbar-track-gap: 0;

      @include scrollbar.customScrollbar;

      &--scroll {
        .table__content {
          margin-bottom: 8px;
        }
        &:active {
          cursor: grabbing;
        }
      }
    }

    &__content {
      position: relative;
      min-width: 100%;
      border: 1px solid $stroke-color--1;
      border-top: 3px solid var(--main-color);
      border-collapse: collapse;
      background: #ffffff;
    }

    :deep() {
      td {
        border: 1px solid $stroke-color--1;
        padding: $spacing-3 $spacing-4;
        color: $text-color--2;
        vertical-align: top;
      }
    }
  }
</style>
